import { useEffect, useMemo, useState } from "react";
import { styles } from "./styles";
import Clickable from "../Clickable";
import Chat from "../Chat";
import type { ChatCuesProps } from "./types";

/**
 * `Chat.Cues` component to display suggested message cues in a chat interface.
 */
function ChatCues({
  options,
  onClick,
  showInitial = 3,
  delay = 300,
  onExpanded,
  onRevealed
}: ChatCuesProps) {
  const [expanded, setExpanded] = useState(false);
  const [isRevealed, setIsRevealed] = useState(!delay);

  const randomizedOptions = useMemo(() => shuffleArray(options), [options]);

  const visibleOptions = useMemo(
    () =>
      expanded ? randomizedOptions : randomizedOptions.slice(0, showInitial),
    [expanded, randomizedOptions, showInitial]
  );

  function onCueClick(option: string) {
    onClick(option);
    setExpanded(false);
  }

  function onExpandClick() {
    setExpanded(!expanded);
    if (!expanded) {
      setTimeout(() => onExpanded?.());
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsRevealed(true);
      setTimeout(() => onRevealed?.());
    }, delay);

    return () => clearTimeout(timeoutId);
  }, []);

  if (!isRevealed) {
    return null;
  }

  return (
    <div
      className="kit-ChatCues"
      aria-expanded={expanded}
      css={[styles.cues.base]}
    >
      <div
        css={[
          styles.cues.optionsWrapper,
          expanded && styles.cues.optionsWrapperExpanded
        ]}
      >
        {visibleOptions.map((option, i) => (
          <Chat.Cue key={`${option}-${i}`} onClick={() => onCueClick(option)}>
            {option}
          </Chat.Cue>
        ))}
      </div>

      {options.length > showInitial && (
        <div
          className="kit-chat-cues-expand-target"
          css={[styles.cues.expandTarget]}
        >
          <Clickable onClick={onExpandClick} aria-expanded={expanded}>
            {expanded ? "Show Less Suggestions" : "Show All Suggestions"}
          </Clickable>
        </div>
      )}
    </div>
  );
}

/**
 * Randomize the order of elements in an array using the Fisher-Yates shuffle algorithm.
 * @param array The array to shuffle.
 * @returns A new array with the elements shuffled.
 */
function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    // Pick a random index from 0 to i
    const j = Math.floor(Math.random() * (i + 1));

    // Swap elements at indices i and j
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default ChatCues;
